
import {Component, Vue, Ref, Watch} from 'vue-property-decorator';
import {Getter, Action, Mutation} from "vuex-class";
import {FormRowWings, SelectWings, WTabs, WTab} from 'wings-ui-components';
import {notifySuccess, notifyError} from "@/utils/notify-helper";
import {dateFilter} from "@/utils/date-util";
import {Duration} from 'luxon';
import SaveTemplate from '@/components/Templates/SaveTemplate.vue';
import SaveTemplateDialog from '@/components/Templates/SaveTemplateDialog.vue';
import Recipients from '@/components/Distributions/Recipients.vue'
import {declOfNum} from '@/utils/data-helper'
import {cloneDeep, isEqual, isEqualWith, omit, omitBy, isNil} from "lodash"
import { transformTransport } from '@/utils/download-helper';
import { MsgTransports } from '@/store/types';
import { getTranslatedParams } from '@/utils/notify-utils';
import { ActionLog } from '@/store/auth/types';

@Component({components: {
  FormRowWings,
  SelectWings,
  WTab,
  WTabs,
  SaveTemplate,
  SaveTemplateDialog,
  Recipients
}})
export default class GenericDistribution extends Vue {
  @Ref('validator') validator: any;
  @Ref('template') templateRef: any;
  @Ref('recipients') recipientsRef: any;
  @Ref('pushFilter') pushFilterRef: any;

  @Getter("originators") originatorsList: any;
  @Getter("blacklists") blackLists: any;
  @Getter("messageTypes") messageTypesList: any;
  @Getter("transportsMultichannel") transportsMultichannelList: any;
  @Getter("multichannelTemplates") multichannelTemplatesList: any;
  @Getter("pushApplications") applicationsList: any;
  @Getter("allowedPages") allowedPages: any;
  @Getter("moderationSettings") moderationSettings: any;

  @Action("getTemplates") actionGetTemplates: any;
  @Action("checkAddressBookContactDuplicates") actionCheckAddressBookContactDuplicates: any;
  @Action("getMultichannelTemplate") actionGetMultichannelTemplate: any;
  @Action("createDistribution") actionCreateDistribution: any;
  @Action("saveTemplate") actionSaveTemplate: any;
  @Action("actionLog") actionLog: any;

  @Getter("templateObject") template: any;
  @Mutation('setTemplateObject') setTemplateObject: any

  protected transport = ''
  logObject: ActionLog = {}

  protected activeTab = 'tab-template'
  protected templatesList = []
  step_recipients = 3;

  protected templateOrig: any = null
  protected duplicatesCount = 0
  protected totalRecipients = 0

  protected distribution: any = {
    templateId: ''
  }
  protected recipients: any = {
    groupsId: [],
    recipient: [],
  }
  protected pushFilter: any = {
    os_ids: '',
    os_version_ids: [],
    sdk_ids: [],
    mapp_ids: [],
    client_filter: null,
    device_filter: null,
  }
  recipientType = 'number' // number or filter

  isShowSaveTemplate = false
  keyName = this.$options.name || ''
  showClearButton = false
  savedData: any = null

  cascadingMultichannelTemplates = []

  get distributionType() {
    return this.template.startDatetime
      ? this.$t('distribution.planned').toString() + dateFilter(this.template.startDatetime)
      : this.$t('distribution.immediate')
  }

  get distributionEnd() {
    return this.template.endDatetime
      ? dateFilter(this.template.endDatetime)
      : this.$t('distribution.notSpecified')
  }

  get distributionTime() {
    return this.template.timeBegin ?
      this.$t('distribution.distributionTime.period', {
        from: this.template.timeBegin,
        to: this.template.timeEnd
      })
      : this.$t('distribution.distributionTime.notSpecified')
  }

  get originatorName() {
    return this.originatorsList.find((item:any) => item.id == this.template.senderId)?.name
  }

  get allowedDays() {
    let days = []
    if (this.template.allowedDays.includes('MON')) days.push(this.$t('week_days.MON'))
    if (this.template.allowedDays.includes('TUE')) days.push(this.$t('week_days.TUE'))
    if (this.template.allowedDays.includes('WED')) days.push(this.$t('week_days.WED'))
    if (this.template.allowedDays.includes('THU')) days.push(this.$t('week_days.THU'))
    if (this.template.allowedDays.includes('FRI')) days.push(this.$t('week_days.FRI'))
    if (this.template.allowedDays.includes('SAT')) days.push(this.$t('week_days.SAT'))
    if (this.template.allowedDays.includes('SUN')) days.push(this.$t('week_days.SUN'))
    return days
  }

  get blackListName() {
    return this.template.blackListId
      ? this.blackLists.find((item:any) => item.id == this.template.blackListId)?.name
      : this.$t('distribution.notSelected')
  }

  get messageTypeName() {
    return this.template.messageTypeId
      ? this.messageTypesList.find((item:any) => item.messageTypeId == this.template.messageTypeId)?.messageTypeName
      : ''
  }

  get multiChannelName() {
    return this.template.multichannelId
      ? this.transportsMultichannelList.find((item:any) => item.id == this.template.multichannelId)?.name
      : ''
  }

  get multiChannelTemplateName() {
    return this.template.multichannelTemplateId
      ? this.multichannelTemplatesList.find((item:any) => item.templateId == this.template.multichannelTemplateId)?.name
      : ''
  }

  getTimeoutHuman(timeout: number) {
    // 1 час 1 минуту 1 секунду
    const intervalArr = Duration.fromMillis(timeout*1000).toISOTime().split('.')[0].split(':')
    const trans = [
      this.$t('declensions.hours').toString().split('/'),
      this.$t('declensions.minutes').toString().split('/'),
      this.$t('declensions.seconds').toString().split('/'),
    ]
    const interval = intervalArr.map((data, i) => {
      const item = Number(data)
      return item ? declOfNum(item, trans[i]) : ''
    })
    return interval.join(' ')
  }

  getTimeout(timeout: number) {
    return Duration.fromMillis(timeout*1000).toISOTime().split('.')[0]
  }

  get multiChannel() {
    if (!this.template.timeout) return this.$t('distribution.multiChannel.disabled')
    // Отправить через 1 час 1 минуту 1 секунду c использованием Call-шаблона IVR
    // Push (<Push-шаблон>)→(<Таймаут>)→ SMS (<SMS-шаблон>)→(<Таймаут>)→ Email (<Email-шаблон>)
    let arr: any = []

    if (this.template.multichannelTemplateId && this.cascadingMultichannelTemplates.length) {
      arr = this.cascadingMultichannelTemplates.map((item: any) => {
        return item.transport + ' (' + item.name + ') ' + (item.timeout ? '→  (' + this.getTimeout(item.timeout) + ')' : '')
      });
    }
    arr.unshift(`${this.transport} (${this.template.name}) → (${this.getTimeout(this.template.timeout)})`)

    return arr.join(' → ')
  }

  @Watch('template.multichannelTemplateId', {immediate: true})
  async onTemplateChangeMultichannel(val: number, oldVal?: number) {
    if (val) {
      try {
        this.cascadingMultichannelTemplates = await this.actionGetMultichannelTemplate({templateId: this.template.multichannelTemplateId})
      } catch(error) {
        notifyError(error);
      }
    }
  }

  get isAllowedTemplateSave() {
    return !this.isTemplate && this.allowedPages.includes(`menu.item.templates.${transformTransport(this.transport)}`)
  }

  get templateOnly() {
    return this.moderationSettings.find((item: any) => item.transportName == this.transport)?.templateOnly
  }

  get isTemplate() {
    if (!this.distribution.templateId) return false
    this.templateOrig?.allowedDays?.sort()
    this.template?.allowedDays?.sort()
    const result = isEqualWith(this.templateOrig, this.template, function customizer(objValue:any, othValue:any) {
      if ((objValue === null && othValue === '') || (objValue === null && othValue === undefined)) return true
      return undefined
    })
    // console.log(result, this.templateOrig, this.template)
    return result
  }

  async validateTemplateTab() {
    if (this.templateOnly) {
      if (await this.validator.validate())
        this.setActiveTab('tab-message')
    } else {
      this.setActiveTab('tab-message')
    }
  }

  async created() {
    try {
      this.templatesList = await this.actionGetTemplates({filter: {status: 'Active'}, transport: this.transport})
    } catch(error) {
      notifyError(error);
    }

    this.savedData = sessionStorage.getItem(this.keyName)
    if (this.savedData) {
      this.savedData = JSON.parse(this.savedData)
      if (this.savedData?.template) this.showClearButton = true
      if (this.savedData?.templateId) {
        this.distribution.templateId = this.savedData.templateId
      } else if (this.savedData?.template) {
        this.setTemplateObject(this.savedData.template)
        this.savedData.template = null
      }
      if (this.savedData?.recipientType) {
        this.recipientType = this.savedData.recipientType
      }
      await this.$nextTick()
      if (this.savedData?.recipients && this.recipientsRef) {
        this.recipientsRef.setState(this.savedData.recipients)
      }
      if (this.savedData?.pushFilter && this.pushFilterRef) {
        this.pushFilterRef.setState(this.savedData.pushFilter)
      }
    }
  }

  beforeLeave(to: any, from: any, next: any) {
    if (to.name != 'Login') {
      this.savedData = {
        templateId: this.distribution.templateId,
        template: !isEqual(this.template, this.templateRef.templateObject) ? this.template : null,
        recipientType: this.recipientType,
        recipients: this.recipientsRef ? this.recipientsRef.getState() : null,
      }
      if (this.pushFilterRef) {
        this.savedData = {...this.savedData, pushFilter: this.pushFilterRef.getState()}
      }

      sessionStorage.setItem(this.keyName, JSON.stringify(this.savedData))
    }
    next()
  }

  clearSavedData() {
    sessionStorage.setItem(this.keyName, '')
    this.savedData = null
    this.showClearButton = false
    this.distribution.templateId = ''
    this.templateRef.setTemplate()
    this.recipientType = 'number'
    if (this.recipientsRef) this.recipientsRef.setState()
    if (this.pushFilterRef) this.pushFilterRef.setState()
  }

  onLoadTemplate(template: any) {
    this.templateOrig = cloneDeep(template)
    if (this.savedData?.template) {
      this.setTemplateObject(Object.assign(template, this.savedData.template))
      this.savedData.template = null
    }
  }

  onTemplateIdChange(val:string) {
    if (val) {
      this.templateRef.genericTemplate.getTemplate(val)
      // this.step_recipients = 2
    } else {
      this.templateRef.setTemplate()
      // this.step_recipients = 3
    }
  }

  setActiveTab(tab: string) {
    this.activeTab = tab
  }

  async validateTemplate() {
    if (!await this.templateRef.genericTemplate.validator.validate()) {
      notifyError(null, this.templateRef.genericTemplate.validator.errors)
      return
    }
    this.setActiveTab('tab-recipients')
  }

  async validateRecipients() {
    this.totalRecipients = this.recipientsRef.totalRecipients
    if (!await this.recipientsRef.validate()) return
    if (!this.recipientsRef.checkDuplicates) {
      this.setActiveTab('tab-final')
      return
    }
    try {
      let data = {
        contacts: this.recipients.recipient,
        groupsId: this.recipients.groupsId,
        checkDuplicatesPolicy: this.recipientsRef.checkDuplicatesPolicy,
        contactsFormat: this.transport == MsgTransports.EMAIL ? 2 : 1
      }
      if (this.transport == MsgTransports.PUSH) {
        // @ts-ignore
        delete data.contactsFormat
      }
      const resp = await this.actionCheckAddressBookContactDuplicates(data)
      this.duplicatesCount = resp.data.duplicatesCount
      this.setActiveTab('tab-final')
    } catch(error) {
      notifyError(error);
    }
  }

  validatePushRecipients() {
    if (this.recipientType == 'number') {
      this.validateRecipients()
    } else {
      this.setActiveTab('tab-final')
    }
  }

  async updateTemplate() {
    try {
      let template = this.template
      template = template.customData ? {...template, customData: JSON.parse(template.customData)} : template
      this.logObject = {
        actionPage: this.$route.meta.msgKey,
        action: "Editing",
        actionStatus: 'success',
        objectType: "Template",
        objectName: this.template.name,
        addititionalInfo: getTranslatedParams(template, {
          distribution: 'distribution.distribution'
        })
      }
      const resp = await this.actionSaveTemplate({template, transport: this.transport})
      await this.actionLog(this.logObject)
      this.templateOrig = cloneDeep(this.template)
      this.isShowSaveTemplate = false
      notifySuccess(this.$t('distribution.messages.templateSaved').toString())
    } catch(error) {
      notifyError(error)
      await this.actionLog({...this.logObject, actionStatus: 'failed'})
    }
  }

  async saveTemplate(name: string) {
    try {
      let template = omit(this.template, ['templateId'])
      template = template.customData ? {...template, customData: JSON.parse(template.customData)} : template
      this.logObject = {
        actionPage: this.$route.meta.msgKey,
        action: "Creation",
        actionStatus: 'success',
        objectType: "Template",
        objectName: name,
        addititionalInfo: getTranslatedParams(template, {
          distribution: 'distribution.distribution'
        })
      }
      const resp = await this.actionSaveTemplate({template: {...template, name}, transport: this.transport})
      await this.actionLog(this.logObject)
      this.distribution.templateId = resp.data.id
      this.template.templateId = resp.data.id
      this.templateOrig = cloneDeep(this.template)
      this.isShowSaveTemplate = false
      notifySuccess(this.$t('distribution.messages.templateSaved').toString())
    } catch(error) {
      notifyError(error)
      await this.actionLog({...this.logObject, actionStatus: 'failed'})
    }
  }

  async createDistribution() {
    const recipients = this.recipientType == 'filter' ? this.pushFilter : this.recipients

    let template = omit(this.template, ['templateId', 'name', 'author', 'status'])
    template = omitBy(template, isNil)
    template = template.customData ? {...template, customData: JSON.parse(template.customData)} : template

    const distribution = this.isTemplate
      ? { templateId: this.distribution.templateId, ...recipients }
      : {...template, ...recipients}

    const distributionLog = this.distribution.templateId
      ? { templateId: this.distribution.templateId, duplicatesCount: this.duplicatesCount, totalRecipients: this.totalRecipients }
      : {...template, duplicatesCount: this.duplicatesCount, totalRecipients: this.totalRecipients}

    try {
      this.logObject = {
        actionPage: this.$route.meta.msgKey,
        action: "Distribution generating",
        actionStatus: 'success',
        objectType: "Distribution",
        addititionalInfo: getTranslatedParams(distributionLog, {
          distribution: 'distribution.distribution'
        })
      }
      const resp = await this.actionCreateDistribution({distribution, transport: this.transport})
      await this.actionLog(this.logObject)
      this.clearSavedData()
      notifySuccess(this.$t('distribution.messages.distributionSaved').toString())
      this.$router.push({name: 'distribution_list'})
    } catch(error) {
      notifyError(error)
      await this.actionLog({...this.logObject, actionStatus: 'failed'})
    }
  }

}
